import Ionicons from "@expo/vector-icons/Ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { spotApi } from "@/api";
import { Logo } from "@/components/brand/logo";
import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { useAuthStore } from "@/state/auth-store";
import { useSessionStore } from "@/state/session-store";
import type { PublicSpot } from "@/types";
import i18n from "@/i18n";
import { shareText } from "@/lib/share-text";

export default function PublicSpotPage() {
  useTranslation();
  const c = useTheme();
  const router = useRouter();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const isAuthed = useAuthStore((s) => s.status === "authenticated");
  const currentVenueName = useSessionStore((s) => s.venue?.name ?? null);

  const [spot, setSpot] = useState<PublicSpot | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    spotApi
      .publicSpot(slug)
      .then(setSpot)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const atVenue =
    isAuthed &&
    !!spot &&
    !!currentVenueName &&
    currentVenueName.toLowerCase() === spot.venueName.toLowerCase();

  function openApp() {
    router.replace(isAuthed ? "/(tabs)/home" : "/(auth)/welcome");
  }

  function getDirections() {
    if (!spot) return;
    const q = encodeURIComponent(spot.venueAddress || spot.venueName);
    void Linking.openURL(
      Platform.OS === "ios"
        ? `http://maps.apple.com/?q=${q}`
        : `https://www.google.com/maps/search/?api=1&query=${q}`,
    );
  }

  function shareSpot() {
    if (!spot) return;
    void shareText({
      message: i18n.t("business.public.shareMessage", {
        venue: spot.venueName,
      }),
    });
  }

  if (loading) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <View style={styles.centered}>
          <ActivityIndicator color={c.tint} />
        </View>
      </SafeAreaView>
    );
  }

  if (notFound || !spot) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <View style={styles.centered}>
          <Ionicons name="location-outline" size={40} color={c.textMuted} />
          <ThemedText variant="subtitle" center>
            {i18n.t("business.public.notFound")}
          </ThemedText>
          <ThemedText variant="body" tone="secondary" center>
            {i18n.t("business.public.notFoundBody")}
          </ThemedText>
        </View>
      </SafeAreaView>
    );
  }

  const statLines = [
    i18n.t("business.public.conversations", {
      count: spot.stats.conversationsStarted,
    }),
    i18n.t("business.public.groups", { count: spot.stats.groupsFormed }),
    i18n.t("business.public.meetups", { count: spot.stats.meetupMoments }),
  ];

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <ScrollView contentContainerStyle={styles.content}>
        <Logo size={24} />

        <View style={[styles.badge, { backgroundColor: c.backgroundSelected }]}>
          <View style={[styles.liveDot, { backgroundColor: c.success }]} />
          <ThemedText variant="caption" tone="tint">
            {i18n.t("business.public.active")}
          </ThemedText>
        </View>

        <ThemedText variant="display">{spot.venueName}</ThemedText>
        <ThemedText variant="body" tone="secondary">
          {spot.welcomeNote || i18n.t("business.public.welcomeFallback")}
        </ThemedText>

        <View
          style={[
            styles.statsCard,
            { backgroundColor: c.surface, borderColor: c.border },
          ]}
        >
          <ThemedText variant="label" tone="muted">
            {i18n
              .t("business.public.thisWeek")
              .toLocaleUpperCase(i18n.resolvedLanguage)}
          </ThemedText>
          {statLines.map((line) => (
            <View key={line} style={styles.statLine}>
              <Ionicons name="ellipse" size={7} color={c.tint} />
              <ThemedText variant="bodyStrong">{line}</ThemedText>
            </View>
          ))}
        </View>

        {spot.meetupSpots.length > 0 ? (
          <View style={styles.meetups}>
            <ThemedText variant="label" tone="muted">
              {i18n
                .t("business.public.goodSpots")
                .toLocaleUpperCase(i18n.resolvedLanguage)}
            </ThemedText>
            <View style={styles.chips}>
              {spot.meetupSpots.map((m) => (
                <View
                  key={m}
                  style={[styles.chip, { backgroundColor: c.surfaceAlt }]}
                >
                  <ThemedText variant="caption" tone="secondary">
                    {m}
                  </ThemedText>
                </View>
              ))}
            </View>
          </View>
        ) : null}

        <View
          style={[
            styles.gate,
            {
              backgroundColor: atVenue ? c.successTint : c.surfaceAlt,
              borderColor: atVenue ? c.success : c.border,
            },
          ]}
        >
          <Ionicons
            name={atVenue ? "checkmark-circle" : "navigate-circle-outline"}
            size={20}
            color={atVenue ? c.success : c.textSecondary}
          />
          <ThemedText variant="caption" tone="secondary" style={{ flex: 1 }}>
            {atVenue
              ? i18n.t("business.public.atVenue", { venue: spot.venueName })
              : i18n.t("business.public.notAtVenue")}
          </ThemedText>
        </View>

        <Pressable
          onPress={openApp}
          style={[styles.primaryBtn, { backgroundColor: c.tint }]}
        >
          <Ionicons name="compass-outline" size={18} color={c.onTint} />
          <ThemedText variant="bodyStrong" tone="onTint">
            {i18n.t("business.public.open")}
          </ThemedText>
        </Pressable>

        <View style={styles.secondaryRow}>
          <Pressable
            onPress={getDirections}
            style={[styles.secondaryBtn, { borderColor: c.border }]}
          >
            <Ionicons name="map-outline" size={17} color={c.tint} />
            <ThemedText variant="caption" tone="tint">
              {i18n.t("business.public.directions")}
            </ThemedText>
          </Pressable>
          <Pressable
            onPress={shareSpot}
            style={[styles.secondaryBtn, { borderColor: c.border }]}
          >
            <Ionicons name="share-social-outline" size={17} color={c.tint} />
            <ThemedText variant="caption" tone="tint">
              {i18n.t("business.public.share")}
            </ThemedText>
          </Pressable>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.md,
    padding: Spacing.xl,
  },
  content: {
    width: "100%",
    maxWidth: 560,
    alignSelf: "center",
    padding: Spacing.xl,
    gap: Spacing.md,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    paddingHorizontal: Spacing.md,
    paddingVertical: 6,
    borderRadius: Radius.pill,
  },
  liveDot: { width: 7, height: 7, borderRadius: 4 },
  statsCard: {
    gap: Spacing.sm,
    padding: Spacing.lg,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  statLine: { flexDirection: "row", alignItems: "center", gap: Spacing.sm },
  meetups: { gap: Spacing.sm },
  chips: { flexDirection: "row", flexWrap: "wrap", gap: Spacing.sm },
  chip: {
    paddingHorizontal: Spacing.md,
    paddingVertical: 6,
    borderRadius: Radius.pill,
  },
  gate: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  primaryBtn: {
    minHeight: 52,
    marginTop: Spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
  },
  secondaryRow: { flexDirection: "row", gap: Spacing.sm },
  secondaryBtn: {
    flex: 1,
    minHeight: 46,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
});
